"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../../../contexts/AuthContext"
import {
  ArrowLeftIcon,
  CheckIcon,
  DocumentIcon,
  EllipsisVerticalIcon,
  FaceSmileIcon,
  MagnifyingGlassIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  PhoneIcon,
  PhotoIcon,
  UserIcon,
  UserPlusIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline"
import EmojiPicker from "./EmojiPicker"
import createWS from "./WebSocketClient"
import { API_BASE_URL as API_BASE } from "../../../config/api"

const normalizePhone = (value) => String(value || "").replace(/\D/g, "")

const getPhoneVariants = (value) => {
  const normalized = normalizePhone(value)
  if (!normalized) return []

  const variants = new Set([normalized])
  if (normalized.startsWith("91") && normalized.length > 10) {
    variants.add(normalized.slice(2))
  } else if (normalized.length === 10) {
    variants.add(`91${normalized}`)
  }

  return Array.from(variants)
}

export default function Chat() {
  const navigate = useNavigate()
  const { user, getUserChats, getChatMessages, sendTemplateMessage, fetchContacts, getAllUsers, assignChatToAgent } = useAuth()
  const isRestrictedRole = ["superadmin", "admin", "reseller", "company"].includes(user?.role)

  useEffect(() => {
    if (isRestrictedRole) {
      navigate(user?.role === "reseller" ? "/dashreseller" : (user?.role === "superadmin" ? "/su-admin" : "/dash-admin"), { replace: true })
    }
  }, [isRestrictedRole, user?.role, navigate])

  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [conversations, setConversations] = useState([])
  const [contactsIndex, setContactsIndex] = useState({})
  const [selectedContact, setSelectedContact] = useState(null)
  const [messages, setMessages] = useState([])
  const [newMessage, setNewMessage] = useState("")
  const [showEmojiPicker, setShowEmojiPicker] = useState(false)
  const [showMediaMenu, setShowMediaMenu] = useState(false)
  const [showMobileChat, setShowMobileChat] = useState(false)
  const [wsConnected, setWsConnected] = useState(false)
  const [notifications, setNotifications] = useState({}) // contactNumber -> unread count
  const [agents, setAgents] = useState([])
  const [assigningChat, setAssigningChat] = useState("")
  const [activeDropdownChatId, setActiveDropdownChatId] = useState(null)
  const fileInputRef = useRef(null)
  const messagesEndRef = useRef(null)
  const wsRef = useRef(null)
  const [isSessionExpired, setIsSessionExpired] = useState(false)
  const [lastUserMessageAt, setLastUserMessageAt] = useState(null)
  const currentUserId = user?._id || user?.id
  const isOwnerUser = user?.role === "user"
  
  // Phone number selector
  const [wabaPhoneNumbers, setWabaPhoneNumbers] = useState([])
  const [selectedPhone, setSelectedPhone] = useState("all")
  const [timeFilter, setTimeFilter] = useState("all")
  const [showSearch, setShowSearch] = useState(false)
  
  // 🔍 find last incoming message from user


  useEffect(() => {
    if (!user?.id && !user?._id) return

    const uid = user.chatUserId || user.id || user._id

    wsRef.current = createWS({
      onOpen: (ws) => {
        console.log("✅ WS connected")
        setWsConnected(true)

        // Subscribe to user's messages
        ws.send(
          JSON.stringify({
            type: "subscribe",
            userId: uid,
          }),
        )
      },
      onMessage: (data) => {
        if (data.type === "message" || data.type === "new_message") {
          console.log("📩 WS message received:", data)

          if (data.type === "message") {
            const msg = data.payload
            const contactNumber = msg.senderId

            if (contactNumber !== selectedContact?.contactNumber) {
              setNotifications((prev) => ({
                ...prev,
                [contactNumber]: (prev[contactNumber] || 0) + 1,
              }))
            }

            setConversations((prev) =>
              prev.map((conv) => {
                if (conv.contactNumber === contactNumber) {
                  return {
                    ...conv,
                    lastMessage: msg.message || msg.text || "",
                    lastMessageAt: new Date(msg.createdAt || Date.now()),
                    lastMessageType: msg.messageType || "text",
                    unreadCount: contactNumber === selectedContact?.contactNumber ? 0 : (conv.unreadCount || 0) + 1,
                  }
                }
                return conv
              }),
            )

            if (contactNumber === selectedContact?.contactNumber) {
                setIsSessionExpired(false)
                setLastUserMessageAt(Date.now())

                const meta = msg.metadata || {}
                let type = msg.messageType || msg.type || "text"
                const interactiveType = meta?.mediaInfo?.interactiveType
            
                if ((type === "interactive" || type === "nfm_reply") && interactiveType === "nfm_reply") {
                  type = "nfm_reply"
                }
            
                const normalized = {
                  id: msg._id || msg.id || Date.now(),
                  text: msg.message || msg.text || "",
                  sender: "contact",
                  timestamp: new Date(msg.createdAt || msg.timestamp || Date.now()),
                  type,
                  status: "received",
                  media: msg.media,
                  wamid: msg.wamid,
                  metadata: meta,
                  raw: msg,
                }


              setMessages((prev) => {
                const exists = prev.some(
                  (m) =>
                    (m.id && normalized.id && m.id === normalized.id) ||
                    (m.wamid && normalized.wamid && m.wamid === normalized.wamid),
                )
                return exists ? prev : [...prev, normalized]
              })
            }
          }
        }
      },
      onClose: () => {
        console.log("❌ WS disconnected")
        setWsConnected(false)
      },
      onError: (err) => {
        console.error("⚠️ WS error:", err)
        setWsConnected(false)
      },
    })

    return () => {
      wsRef.current?.close()
      wsRef.current = null
      setWsConnected(false)
    }
  }, [user, selectedContact])

  useEffect(() => {
    let isMounted = true

    async function loadAgents() {
      if (!isOwnerUser || !currentUserId) {
        if (isMounted) setAgents([])
        return
      }

      const result = await getAllUsers()
      if (!isMounted) return

      if (result.success) {
        setAgents(
          (result.users || []).filter(
            (candidate) =>
              candidate?.role === "agent" &&
              String(candidate.createdBy?._id || candidate.createdBy || "") === String(currentUserId),
          ),
        )
      }
    }

    loadAgents()
    return () => {
      isMounted = false
    }
  }, [isOwnerUser, currentUserId, getAllUsers])

  // Load WABA phone numbers for the selector
  useEffect(() => {
    const loadWabaPhoneNumbers = async () => {
      try {
        const token = localStorage.getItem("token") || sessionStorage.getItem("token")
        const response = await fetch(`${API_BASE}/api/waba/accounts`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (response.ok) {
          const data = await response.json()
          const phones = []
          ;(data.accounts || []).forEach((acc) => {
            ;(acc.phoneNumbers || acc.phone_numbers || []).forEach((ph) => {
              const num = ph.display_phone_number || ph.phone_number || ph.number || ""
              const id = ph.id || ph.phone_number_id || ""
              if (num || id) phones.push({ label: num || id, value: num.replace(/\D/g, "") || id })
            })
          })
          // Fallback: use user's cloudNumber / karix number
          if (phones.length === 0) {
            const fallback = user?.cloudNumber || user?.number || ""
            if (fallback) phones.push({ label: fallback, value: fallback.replace(/\D/g, "") })
          }
          setWabaPhoneNumbers(phones)
          // Auto-select first number as default
          if (phones.length > 0) setSelectedPhone(phones[0].value)
        }
      } catch (e) {
        console.error("Failed to load WABA phone numbers:", e)
      }
    }
    if (user?.id || user?._id) loadWabaPhoneNumbers()
  }, [user])

  // Load conversations
  useEffect(() => {
    let isMounted = true
    async function load() {
      if (!user?.id && !user?._id) return
      setLoading(true)
      try {
        const uid = user.id || user._id
        const res = await getUserChats(uid)
        const rows = res?.data || []

        try {
          const list = await fetchContacts()
          const idx = {}
          ;(list || []).forEach((c) => {
            if (c?.phone) idx[String(c.phone)] = c?.name || ""
          })
          if (isMounted) setContactsIndex(idx)
        } catch (_) {}

        if (isMounted) {
          setConversations(rows)

          const initialNotifications = {}
          rows.forEach((conv) => {
            if (conv.unreadCount > 0) {
              initialNotifications[conv.contactNumber] = conv.unreadCount
            }
          })
          setNotifications(initialNotifications)
        }
      } catch (e) {
        console.error("load conversations error:", e)
        if (isMounted) setConversations([])
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    load()
    return () => {
      isMounted = false
    }
  }, [user, getUserChats, fetchContacts])

  const loadMessages = async (contactNumber) => {
    if (!user?.id && !user?._id) return
    const uid = user.id || user._id
    try {
      console.log("[Chat] Fetching messages for:", { userId: uid, contactNumber })
      const res = await getChatMessages(uid, String(contactNumber).replace(/\D/g, ""))
      const data = res?.data || []

      const contactVariants = getPhoneVariants(contactNumber)
      const normalized = data.map((m) => {
  const id = m._id || m.wamid || `${m.createdAt}-${Math.random()}`
  const isFromContact = contactVariants.includes(normalizePhone(m.senderId))

  const meta = m.metadata || {}
  let type = m.messageType || "text"

// 🔥 FIX: mark templates properly
    if (m.messageType === "template") {
      type = "template"
    }
  const interactiveType = meta?.mediaInfo?.interactiveType

  // convert interactive nfm_reply → its own type
  if ((type === "interactive" || type === "nfm_reply") && interactiveType === "nfm_reply") {
    type = "nfm_reply"
  }

  const text = m.message || ""
  const media = m.media ? absoluteMedia(m.media) : null

 return {
  id,
  sender: isFromContact ? "contact" : "user",
  type, // ← now template works
  text: m.message || "",
  buttons: m.meta?.buttons || m.buttons || [],
  templateName: m.meta?.templateName || m.templateName,
  templateLanguage: m.meta?.templateLanguage || "en",
  timestamp: m.createdAt || m.updatedAt,
  status: m.status,
  metadata: meta,
  raw: m,
}
})
      setMessages(normalized)
      scrollToBottom()
      
      const lastUserMsg = [...normalized]
          .reverse()
          .find((m) => m.sender === "contact")
        
        if (lastUserMsg?.timestamp) {
          const lastTime = new Date(lastUserMsg.timestamp).getTime()
          const now = Date.now()
        
          const diffHours = (now - lastTime) / (1000 * 60 * 60)
        
          setLastUserMessageAt(lastTime)
          setIsSessionExpired(diffHours > 24)
        } else {
          // No incoming user message ever → expired
          setIsSessionExpired(true)
        }

      setNotifications((prev) => {
        const updated = { ...prev }
        delete updated[contactNumber]
        return updated
      })
    } catch (e) {
      console.error("[Chat] loadMessages error:", e)
      setMessages([])
    }
  }

  const absoluteMedia = (path) => {
    if (!path) return null
    if (path.startsWith("http")) return path
    return `${API_BASE}${path.startsWith("/") ? path : `/${path}`}`
  }

  const handleSelect = (row) => {
    const contactNumber = String(row.contactNumber || row.phone || "").replace(/\D/g, "")
    const contactName = row.contactName || contactsIndex[contactNumber] || row.name || contactNumber || "Unknown"

    const selectedRow = { contactNumber, contactName }
    setSelectedContact(selectedRow)
    setShowMobileChat(true)
    setMessages([])
    loadMessages(contactNumber)

    setNotifications((prev) => {
      const updated = { ...prev }
      delete updated[contactNumber]
      return updated
    })
  }

  const handleBack = () => {
    setShowMobileChat(false)
    setSelectedContact(null)
    setMessages([])
  }

  const getAssignedAgentId = (conversation) => {
    return String(conversation?.assignedAgent?._id || conversation?.assignedAgentId || conversation?.assignedAgent || "")
  }

  const handleAgentAssignment = async (agentId, conversation) => {
    if (!isOwnerUser || !currentUserId) return

    const contactNumber = String(conversation.contactNumber || "").replace(/\D/g, "")
    const rowKey = conversation.conversationId || contactNumber

    if (!contactNumber) return

    setAssigningChat(rowKey)
    const result = await assignChatToAgent(currentUserId, contactNumber, agentId)

    if (result.success) {
      const assignedAgent = agentId ? agents.find((agent) => String(agent._id) === String(agentId)) || null : null
      setConversations((prev) =>
        prev.map((item) => {
          const itemKey = item.conversationId || item.contactNumber
          if (String(itemKey) !== String(rowKey)) return item
          return {
            ...item,
            assignedAgent,
            assignedAgentId: agentId || null,
          }
        }),
      )
    } else {
      console.error("Chat assignment failed:", result.error)
    }

    setAssigningChat("")
  }

  const getAssignedAgentInfo = (conversation) => {
    const agentId = getAssignedAgentId(conversation)
    if (!agentId) return null

    const agentFromList = agents.find((a) => String(a._id) === String(agentId))
    if (agentFromList) {
      const name = agentFromList.username || agentFromList.email || "Agent"
      return {
        id: agentId,
        name,
        initial: name.charAt(0).toUpperCase(),
      }
    }

    const nestedAgent = conversation?.assignedAgent
    if (nestedAgent && typeof nestedAgent === "object") {
      const name = nestedAgent.username || nestedAgent.email || "Agent"
      return {
        id: agentId,
        name,
        initial: name.charAt(0).toUpperCase(),
      }
    }

    return {
      id: agentId,
      name: "Agent",
      initial: "A",
    }
  }

  const handleSend = async () => {
    if (!selectedContact || !newMessage.trim()) return

    const messageText = newMessage.trim()
    const optimistic = {
      id: `tmp-${Date.now()}`,
      sender: "user",
      type: "text",
      text: messageText,
      timestamp: new Date().toISOString(),
      status: "sending",
    }

    setMessages((prev) => [...prev, optimistic])
    setNewMessage("")
    setShowEmojiPicker(false)
    scrollToBottom()

    try {
      const response = await sendTemplateMessage(
        selectedContact.contactNumber,
        "hello_world", // Using default template
        "en",
        [
          {
            type: "body",
            parameters: [{ type: "text", text: messageText }],
          },
        ],
        user?.id || user?._id,
      )

      if (!response?.success || response.data?.success === false) {
        throw new Error(response?.error || response.data?.error || response.data?.message || "Failed to send message")
      }

      console.log("[Chat] Message sent successfully:", response)
      const responseData = response.data?.data || response.data || {}
      const sentChatMessage = responseData.chatMessage || {}
      const persistedId = sentChatMessage._id || responseData.messageId

      // Update optimistic message status
      setMessages((prev) =>
        prev.map((m) =>
          m.id === optimistic.id
            ? {
                ...m,
                status: sentChatMessage.status || responseData.status || "sent",
                id: persistedId || m.id,
                timestamp: sentChatMessage.createdAt || responseData.sentAt || m.timestamp,
                raw: sentChatMessage._id ? sentChatMessage : m.raw,
              }
            : m,
        ),
      )

      setConversations((prev) =>
        prev.map((conv) => {
          if (getPhoneVariants(conv.contactNumber).includes(normalizePhone(selectedContact.contactNumber))) {
            return {
              ...conv,
              lastMessage: messageText,
              lastMessageAt: new Date(),
              lastMessageType: "text",
            }
          }
          return conv
        }),
      )
    } catch (e) {
      console.error("[Chat] send message error:", e)
      setMessages((prev) => prev.map((m) => (m.id === optimistic.id ? { ...m, status: "failed" } : m)))
    }
  }

  const handleFilePick = () => {
    setShowMediaMenu(false)
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0]
    e.target.value = ""
    if (!file || !selectedContact) return

    const kind = file.type.startsWith("image/") ? "image" : file.type.startsWith("video/") ? "video" : "document"
    const tmp = {
      id: `tmp-${Date.now()}`,
      sender: "user",
      type: kind,
      text: file.name,
      file: URL.createObjectURL(file),
      timestamp: new Date().toISOString(),
      status: "sent",
    }
    setMessages((prev) => [...prev, tmp])
  }

  const handleEmojiSelect = (emoji) => {
    setNewMessage((prev) => prev + emoji)
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }

  const filteredConversations = useMemo(() => {
    const term = searchTerm.toLowerCase()
    return (conversations || []).filter((c) => {
      const name = c.contactName || contactsIndex[String(c.contactNumber)] || String(c.contactNumber) || ""
      const matchesSearch = name.toLowerCase().includes(term) || String(c.contactNumber).includes(term)
      // Filter by selected phone number
      const normBusiness = String(c.businessNumber || "").replace(/\D/g, "")
      const matchesPhone = selectedPhone === "all" || !selectedPhone
        ? true
        : !normBusiness
          ? true
          : normBusiness.includes(selectedPhone) ||
            selectedPhone.includes(normBusiness) ||
            (normBusiness.length >= 6 && selectedPhone.includes(normBusiness.slice(-6)))
          
      // Filter by time
      let matchesTime = true
      if (timeFilter !== "all" && c.lastMessageAt) {
        const lastTime = new Date(c.lastMessageAt).getTime()
        const now = Date.now()
        const diffHours = (now - lastTime) / (1000 * 60 * 60)
        if (timeFilter === "24h") matchesTime = diffHours <= 24
        else if (timeFilter === "7d") matchesTime = diffHours <= 24 * 7
      }

      return matchesSearch && matchesPhone && matchesTime
    })
  }, [conversations, contactsIndex, searchTerm, selectedPhone, timeFilter])

  const groupedMessages = useMemo(() => {
    const groups = {}
    messages.forEach((message) => {
      const dateObj = new Date(message.timestamp)
      const date = dateObj.toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" })
      if (!groups[date]) {
        groups[date] = []
      }
      groups[date].push(message)
    })
    return groups
  }, [messages])

  const formatTime = (d) => new Date(d).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })

  const formatDate = (dateStr) => {
    // Return the month-wise string directly (e.g., "July 2026")
    return dateStr
  }
  
  function FlowReplyMessage({ m }) {
  const [open, setOpen] = useState(false)

  const meta = m.metadata || {}
  const mediaInfo = meta.mediaInfo || {}
  const original = meta.originalPayload || meta.original || m.raw?.webhookData || {}
  const originalInteractive = original.interactive || {}

  // Try all locations where nfm_reply might live
  const payload =
    mediaInfo.nfmReply ||
    mediaInfo.payload?.nfm_reply ||
    originalInteractive.nfm_reply ||
    original.nfm_reply ||
    {}

  const body = payload.body || "View Flow"
  let response = {}

  try {
    if (payload.response_json) {
      // Sometimes response_json is a string, sometimes already an object
      response =
        typeof payload.response_json === "string"
          ? JSON.parse(payload.response_json)
          : payload.response_json
    }
  } catch (e) {
    console.error("Failed to parse flow response_json", e)
  }

  const time = new Date(m.timestamp || m.createdAt || Date.now()).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })

  return (
    <div className="max-w-xs md:max-w-md">
      {/* Bubble that looks like WhatsApp "View Flow" */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left focus:outline-none"
      >
        <div className="bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2 shadow-sm">
          <div className="text-xs text-gray-500 dark:text-gray-300 mb-1">
            Flow response
          </div>
          <div className="flex items-center justify-between gap-2">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {body || "View Flow"}
              </div>
              <div className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                Response received • View flow
              </div>
            </div>
          </div>
        </div>
      </button>

      <div className="mt-1 text-xs text-gray-400 text-right">
        {time}
      </div>

      {/* Modal with full form response */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Form response
              </h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 text-lg leading-none"
              >
                ×
              </button>
            </div>

            <div className="p-4 overflow-y-auto">
              {(!response || Object.keys(response).length === 0) ? (
                <p className="text-sm text-gray-500 dark:text-gray-300">
                  No response data.
                </p>
              ) : (
                <dl className="space-y-3">
                  {Object.entries(response).map(([key, value]) => (
                    <div key={key}>
                      <dt className="text-xs uppercase tracking-wide text-gray-400 dark:text-gray-500">
                        {key}
                      </dt>
                      <dd className="text-sm text-gray-800 dark:text-gray-100 break-words">
                        {Array.isArray(value) ? value.join(", ") : String(value)}
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </div>

            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="px-4 py-1.5 text-sm rounded-lg bg-green-500 text-white hover:bg-green-600 transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


  
    function ContactMessage({ m }) {
  // Try multiple places where contact payload might live
  const contacts = m.webhookData?.contacts || m.metadata?.mediaInfo?.contacts || m.contacts || m.payload?.contacts || []
  const contact = (contacts && contacts.length && contacts[0]) || null

  const name =
    contact?.name?.formatted_name ||
    contact?.name?.first_name ||
    contact?.name?.formatted ||
    m.text ||
    "Contact"

  // phones array may be shape: [{ phone: "+91...", type: "CELL", wa_id: "91853..." }]
  const phones = contact?.phones || contact?.phone || []
  
  // Extract phone number for display
  const phone = phones.length > 0 ? (phones[0].phone || phones[0].value || phones[0].wa_id || "") : ""
  
  // Clean phone number for tel: link
  const phoneClean = phone.replace(/\D/g, "")

  // build vCard string for download
  function buildVCard() {
    if (!contact) return null
    const fullName = name
    const telLines = (phones || []).map((p, i) => {
      const number = p.phone || p.value || p.wa_id || ""
      const type = (p.type || "CELL").toUpperCase()
      return `TEL;TYPE=${type}:${number}`
    }).join("\n")

    const org = (contact.org && contact.org.company) || ""
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${fullName}`,
      org ? `ORG:${org}` : "",
      telLines,
      "END:VCARD",
    ].filter(Boolean).join("\n")

    return vcard
  }

  const vcard = buildVCard()
  const vcardHref = vcard ? `data:text/vcard;charset=utf-8,${encodeURIComponent(vcard)}` : null

  if (!contact) {
    return (
      <div className="p-3 bg-white rounded-lg shadow-sm max-w-md">
        <div className="text-sm text-gray-700">Contact information not available</div>
      </div>
    )
  }
  
   return (
    <div className="max-w-xs bg-white dark:bg-gray-700 p-4 rounded-xl shadow-md border border-gray-200 dark:border-gray-600">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white text-xl font-semibold">
          {name.charAt(0)}
        </div>

        <div>
          <p className="font-medium text-gray-900 dark:text-white text-sm">{name}</p>
          <p className="text-gray-600 dark:text-gray-300 text-xs">{phone}</p>
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <a
          href={`tel:${phoneClean}`}
          className="text-xs px-3 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 transition"
        >
          Call
        </a>
      </div>

      <div className="mt-1 text-right text-xs text-gray-400">
        {new Date(m.timestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </div>
  );
}
  
  function PdfIcon({ className = "w-7 h-7" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="24" height="24" rx="4" fill="#E6EEF8"/>
      <path d="M8 7h5l3 3v7a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2z" fill="#fff" />
      <path d="M13 7v3a1 1 0 0 0 1 1h3" stroke="#2B6CB0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.5 14.5h5" stroke="#2B6CB0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M9.5 11.5h5" stroke="#2B6CB0" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

  
  // Add near top or with other helpers in Chat.jsx
function DocumentMessage({ m }) {
  // m.file or m.media should be an absolute backend URL for uploaded media.
  const fileUrl = m.file || m.media || null;
  const filename = m.text || (fileUrl ? fileUrl.split("/").pop() : "document.pdf");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);

  // optional: if you store file size in message metadata, display it
  const filesize = m.metadata?.mediaInfo?.sizeReadable || null; // e.g. "120 KB"

  if (!fileUrl) {
    return (
      <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm max-w-md">
        <PdfIcon />
        <div className="flex-1">
          <div className="text-sm font-medium text-gray-800">Document unavailable</div>
          <div className="text-xs text-gray-500">File not downloaded yet</div>
        </div>
      </div>
    );
  }
  

  return (
   <div className="max-w-md w-full">
      {/* File card */}
      <div className="flex items-center gap-3 p-3 bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 flex items-center justify-center bg-blue-50 rounded-md">
            <PdfIcon className="w-8 h-8"/>
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3">
            <div className="overflow-hidden">
              <div className="text-sm font-semibold text-gray-800 truncate">{filename}</div>
              {filesize && <div className="text-xs text-gray-500 mt-0.5">{filesize}</div>}
            </div>

            <div className="flex items-center gap-2">
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="text-sm px-3 py-1 border border-blue-100 bg-blue-50 text-blue-700 rounded-md hover:bg-blue-100"
                title="Open / Download"
              >
                Open
              </a>

              <button
                onClick={() => setExpanded((s) => !s)}
                className="text-sm px-3 py-1 border border-gray-100 bg-white text-gray-700 rounded-md hover:bg-gray-50"
                title={expanded ? "Close preview" : "Preview"}
              >
                {expanded ? "Close" : "Preview"}
              </button>
            </div>
          </div>

          {/* Small inline preview (collapsed by default) */}
          <div className="mt-3">
            <div className="rounded-md overflow-hidden border border-gray-100 bg-gray-50">
              {/* default small preview area */}
              <div style={{ height: expanded ? 480 : 140 }} className="w-full">
                <iframe
                  src={fileUrl}
                  title={filename}
                  className="w-full h-full"
                  style={{ border: "none" }}
                />
              </div>
            </div>

            {/* small toolbar under preview */}
            <div className="mt-2 flex items-center justify-between">
              <div className="text-xs text-gray-400">{/* timestamp if needed */}</div>
              <div className="flex items-center gap-2">
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="text-xs underline text-gray-600"
                >
                  Download
                </a>
                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs underline text-gray-600"
                >
                  Open in new tab
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* small caption/time area */}
      <div className="mt-1 text-xs text-gray-400 ml-1">
        {new Date(m.timestamp || m.createdAt || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
      </div>
    </div>
  );
}


  const DeliveryStatus = ({ status }) => {
    if (status === "sending")
      return <div className="w-4 h-4 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
    if (status === "sent") return <CheckIcon className="w-4 h-4 text-gray-400" />
    if (status === "delivered")
      return (
        <div className="flex">
          <CheckIcon className="w-4 h-4 text-blue-500 -mr-1" />
          <CheckIcon className="w-4 h-4 text-blue-500" />
        </div>
      )
    if (status === "failed") return <span className="text-xs text-red-500">Failed</span>
    return null
  }

  return (
    <div className="h-screen bg-gradient-to-br from-yellow-50 via-green-50 to-white dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex">
      {!wsConnected && (
        <div className="fixed top-0 left-0 right-0 bg-yellow-0  text-yellow-800 px-0 py-0 text-sm z-50 text-right">
          Connecting...
        </div>
      )}

      {/* Left: Conversations */}
      <div
        className={`w-full md:w-1/3 lg:w-1/4 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col min-h-0 ${
          showMobileChat ? "hidden md:flex" : "flex"
        } ${!wsConnected ? "mt-10" : ""}`}
      >
        <div className="p-3 border-b border-gray-200 dark:border-gray-700 space-y-2">
          {/* Top Controls Row */}
          <div className="flex items-center gap-2">
            {/* Phone Number Selector */}
            {wabaPhoneNumbers.length > 0 ? (
              <div className="relative flex-1 min-w-0">
                <select
                  value={selectedPhone}
                  onChange={(e) => setSelectedPhone(e.target.value)}
                  className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer font-medium truncate"
                >
                  <option value="all">📱 All</option>
                  {wabaPhoneNumbers.map((ph, i) => (
                    <option key={i} value={ph.value}>
                      📞 {ph.label}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="flex-1"></div>
            )}

            {/* Filter Dropdown */}
            <div className="relative flex-shrink-0">
              <select
                value={timeFilter}
                onChange={(e) => setTimeFilter(e.target.value)}
                className="pl-8 pr-8 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer font-medium"
                title="Filter by time"
              >
                <option value="all">All Time</option>
                <option value="24h">Last 24h</option>
                <option value="7d">Last 7d</option>
              </select>
              <FunnelIcon className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>

            {/* Search Toggle Button */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`flex-shrink-0 p-2 rounded-xl border transition-colors duration-200 ${
                showSearch
                  ? "bg-green-50 border-green-500 text-green-600 dark:bg-green-900/30 dark:border-green-500 dark:text-green-400"
                  : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-600"
              }`}
              title="Search chats"
            >
              <MagnifyingGlassIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Search Box */}
          {showSearch && (
            <div className="relative animate-in slide-in-from-top-2 duration-200">
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-200"
                autoFocus
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              <p>No chats found</p>
            </div>
          ) : (
            filteredConversations.map((c) => {
              const number = c.contactNumber
              const name = c.contactName || contactsIndex[String(number)] || String(number) || "Unknown"
              const preview =
                c.lastMessageType === "image"
                  ? "📷 Image"
                  : c.lastMessageType === "video"
                    ? "🎥 Video"
                    : c.lastMessage || ""

              const unreadCount = notifications[number] || c.unreadCount || 0

              return (
                <div
                  key={c.conversationId || number}
                  onClick={() => handleSelect(c)}
                  className={`p-4 border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-200 relative ${
                    selectedContact?.contactNumber === String(number)
                      ? "bg-green-50 dark:bg-green-900/20 border-l-4 border-l-green-500"
                      : ""
                  }`}
                >
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-green-500 rounded-full flex items-center justify-center mr-3 shadow-lg relative flex-shrink-0">
                      <span className="text-white font-semibold text-lg">{name?.charAt(0)?.toUpperCase() || "U"}</span>
                      {unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 bg-green-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1">
                          {unreadCount > 99 ? "99+" : unreadCount}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <h3
                          className={`font-semibold text-gray-900 dark:text-white truncate ${unreadCount > 0 ? "font-bold" : ""}`}
                        >
                          {name}
                        </h3>
                        <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0 ml-2">
                          {c.lastMessageAt
                            ? new Date(c.lastMessageAt).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : ""}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <p
                          className={`text-sm text-gray-600 dark:text-gray-400 truncate pr-2 flex-1 ${unreadCount > 0 ? "font-medium text-gray-900 dark:text-white" : ""}`}
                        >
                          {preview}
                        </p>
                        {(() => {
                          const agentInfo = getAssignedAgentInfo(c)
                          if (isOwnerUser) {
                            const isDropdownOpen = activeDropdownChatId === (c.conversationId || number)
                            return (
                              <div className="relative flex-shrink-0" onClick={(event) => event.stopPropagation()}>
                                {agentInfo ? (
                                  <button
                                    type="button"
                                    onClick={() => setActiveDropdownChatId(isDropdownOpen ? null : (c.conversationId || number))}
                                    title={`Assigned to ${agentInfo.name} (Click to change)`}
                                    className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 flex items-center justify-center text-[10px] font-bold border border-blue-200 dark:border-blue-800 hover:scale-105 transition-all shadow-sm"
                                  >
                                    {agentInfo.initial}
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setActiveDropdownChatId(isDropdownOpen ? null : (c.conversationId || number))}
                                    title="Unassigned (Click to assign)"
                                    className="w-6 h-6 rounded-full border border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center text-gray-400 hover:text-green-500 hover:border-green-500 dark:hover:text-green-400 dark:hover:border-green-400 transition-colors shadow-sm"
                                  >
                                    <UserPlusIcon className="w-3.5 h-3.5" />
                                  </button>
                                )}

                                {isDropdownOpen && (
                                  <>
                                    <div
                                      className="fixed inset-0 z-20 cursor-default"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setActiveDropdownChatId(null)
                                      }}
                                    />
                                    <div
                                      className="absolute right-0 mt-1 w-48 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl z-30 py-1 overflow-hidden"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <div className="px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                        Assign Agent
                                      </div>
                                      <div className="max-h-40 overflow-y-auto">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            handleAgentAssignment("", c)
                                            setActiveDropdownChatId(null)
                                          }}
                                          className="w-full text-left px-3 py-2 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20 border-b border-gray-100 dark:border-gray-700/50 flex items-center gap-1.5"
                                        >
                                          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
                                          Unassigned
                                        </button>
                                        {agents.map((agent) => {
                                          const isSelected = agentInfo && String(agent._id) === String(agentInfo.id)
                                          return (
                                            <button
                                              key={agent._id}
                                              type="button"
                                              onClick={() => {
                                                handleAgentAssignment(agent._id, c)
                                                setActiveDropdownChatId(null)
                                              }}
                                              className={`w-full text-left px-3 py-2 text-xs text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-between ${
                                                isSelected ? "bg-green-50 dark:bg-green-950/20 font-semibold text-green-700 dark:text-green-400" : ""
                                              }`}
                                            >
                                              <span className="truncate">{agent.username || agent.email}</span>
                                              {isSelected && <CheckIcon className="w-3.5 h-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />}
                                            </button>
                                          )
                                        })}
                                      </div>
                                    </div>
                                  </>
                                )}
                              </div>
                            )
                          } else if (agentInfo) {
                            return (
                              <div
                                title={`Assigned to ${agentInfo.name}`}
                                className="w-6 h-6 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 flex items-center justify-center text-[10px] font-bold border border-blue-200 dark:border-blue-800 flex-shrink-0 shadow-sm"
                              >
                                {agentInfo.initial}
                              </div>
                            )
                          }
                          return null
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* Right: Chat area */}
      <div
        className={`flex-1 flex flex-col min-h-0 ${!showMobileChat ? "hidden md:flex" : "flex"} ${!wsConnected ? "mt-10" : ""}`}
      >
        {!selectedContact ? (
          <div className="flex-1 flex items-center justify-center relative min-h-0 overflow-hidden">
            <div
              className="absolute inset-0 dark:hidden bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/whatsapp-chat-background-pattern-light-theme.jpg')`,
              }}
            />
            <div
              className="absolute inset-0 hidden dark:block bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `url('/whatsapp-chat-background-pattern-dark-theme.jpg')`,
              }}
            />
            <div className="relative z-10 flex flex-col items-center text-center p-8 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl shadow-lg">
              <div className="w-32 h-32 bg-gradient-to-br from-yellow-500 to-green-500 rounded-full flex items-center justify-center mb-6 shadow-xl">
                <svg className="w-16 h-16 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">WhatsApp Business</h3>
              <p className="text-gray-600 dark:text-gray-300 text-lg">Select a chat to view messages</p>
              {wsConnected && (
                <div className="mt-4 flex items-center text-green-600 dark:text-green-400">
                  <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                  <span className="text-sm">Connected</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={handleBack}
                    className="md:hidden p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200 mr-2"
                  >
                    <ArrowLeftIcon className="w-5 h-5" />
                  </button>
                  <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-green-500 rounded-full flex items-center justify-center mr-3 shadow-lg">
                    <span className="text-white font-semibold">
                      {selectedContact.contactName?.charAt(0)?.toUpperCase() || "U"}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">{selectedContact.contactName}</h3>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                      <span>{selectedContact.contactNumber}</span>
                      {wsConnected && (
                        <>
                          <span className="mx-2">•</span>
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
                            <span>Online</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200">
                    <PhoneIcon className="w-5 h-5" />
                  </button>
                  <button className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200">
                    <EllipsisVerticalIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Messages (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-2 space-y-4 relative min-h-0 pb-4">
              <div
                className="absolute inset-0 dark:hidden bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none"
                style={{
                  backgroundImage: `url('/whatsapp-chat-bubble-pattern-light.jpg')`,
                }}
              />
              <div
                className="absolute inset-0 hidden dark:block bg-cover bg-center bg-no-repeat opacity-10 pointer-events-none"
                style={{
                  backgroundImage: `url('/whatsapp-chat-bubble-pattern-dark.jpg')`,
                }}
              />
              <div className="relative z-10">
                {Object.entries(groupedMessages).map(([dateStr, dayMessages]) => (
                  <div key={dateStr} className="mb-6">
                    {/* Date separator */}
                    <div className="flex justify-center mb-4">
                      <div className="bg-white dark:bg-gray-700 px-3 py-1 rounded-lg shadow-sm border border-gray-200 dark:border-gray-600">
                        <span className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                          {formatDate(dateStr)}
                        </span>
                      </div>
                    </div>

                    {/* Messages for this date */}
                    {dayMessages.map((m) => (
                      <div key={m.id} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"} mb-4`}>
                        <div
                          className={`max-w-xs md:max-w-md px-4 py-2 rounded-2xl shadow-lg ${
                            m.sender === "user"
                              ? "bg-green-500 text-white rounded-br-md"
                              : "bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 border border-gray-200 dark:border-gray-600 rounded-bl-md"
                          }`}
                        >
                          {m.type === "image" && (
                            <img
                              src={m.file || "/placeholder.svg?height=200&width=300&query=shared image"}
                              alt="Shared image"
                              className="rounded-lg mb-2 max-w-full"
                            />
                          )}
                          {m.type === "video" && <video src={m.file} controls className="rounded-lg mb-2 max-w-full" />}
                          {m.type === "document" && (
  <DocumentMessage m={m} />
)}

{m.type === "nfm_reply" && <FlowReplyMessage m={m} />}

{(m.type === "contacts" || m.messageType === "contacts" || m.type === "contact") && (
  <ContactMessage m={m} />
)}

{m.type === "button" && (
  <div className="bg-gray-200 px-3 py-2 rounded-lg max-w-xs">
    🟦 User selected:
    <b className="ml-1">
      {m.metadata?.originalPayload?.button?.text ||
       m.metadata?.button?.text ||
       m.raw?.webhookData?.button?.text ||
       m.text}
    </b>
  </div>
)}

{m.type === "template" && (
  <div className="bg-green-500 text-white px-4 py-3 rounded-xl max-w-md">
    <div className="font-semibold flex items-center gap-2">
      📄 Template message
    </div>

    <div className="mt-1 text-sm opacity-90">
      {m.text || "Template content not stored"}
    </div>

    <div className="text-xs mt-2 opacity-75">
      {m.templateName} ({m.templateLanguage})
    </div>
  </div>
)}

                          {m.type === "text" && <p className="whitespace-pre-wrap">{m.text}</p>}
                          <div
                            className={`flex items-center justify-end gap-1 mt-1 ${
                              m.sender === "user" ? "text-green-100" : "text-gray-500 dark:text-gray-400"
                            }`}
                          >
                            <p className="text-xs">{formatTime(m.timestamp)}</p>
                            {m.sender === "user" && <DeliveryStatus status={m.status} />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
            </div>

            {/* Composer (fixed bottom) */}
            <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <button
                    onClick={() => setShowEmojiPicker((s) => !s)}
                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <FaceSmileIcon className="w-6 h-6" />
                  </button>
                  {showEmojiPicker && (
                    <div className="absolute bottom-12 left-0 z-50">
                      <EmojiPicker onEmojiSelect={handleEmojiSelect} onClose={() => setShowEmojiPicker(false)} />
                    </div>
                  )}
                </div>

                <div className="relative">
                  <button
                    onClick={() => setShowMediaMenu((s) => !s)}
                    className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-200"
                  >
                    <PaperClipIcon className="w-6 h-6" />
                  </button>
                  {showMediaMenu && (
                    <div className="absolute bottom-12 left-0 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl p-2 z-50">
                      <button
                        onClick={handleFilePick}
                        className="flex items-center gap-3 w-full p-3 text-left hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg transition-colors duration-200"
                      >
                        <PhotoIcon className="w-5 h-5 text-blue-500" />
                        <span className="text-gray-900 dark:text-white">Photo / Video / Document</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex-1">
                 {/* 🔒 Session expired notice */}
{isSessionExpired && (
  <div className="mb-2 flex items-center justify-center gap-2 text-xs text-red-500">
    ⛔ User session expired (24h window closed).  
    Waiting for user message to re-enable chat.
  </div>
)}

<div
  className={`flex items-center gap-2 p-2 rounded-xl border
    ${
      isSessionExpired
        ? "bg-gray-100 border-gray-300"
        : "bg-white border-gray-200"
    }
  `}
>
  {/* Emoji */}
{/*  <button
    disabled={isSessionExpired}
    className={`p-2 rounded-lg
      ${
        isSessionExpired
          ? "text-gray-400 cursor-not-allowed"
          : "text-gray-600 hover:bg-gray-100"
      }
    `}
  >
    😊
  </button>
*/}

  {/* Attachment */}
  {/*
  <button
    disabled={isSessionExpired}
    className={`p-2 rounded-lg
      ${
        isSessionExpired
          ? "text-gray-400 cursor-not-allowed"
          : "text-gray-600 hover:bg-gray-100"
      }
    `}
  >
    📎
  </button>
  */}

  {/* Input */}
  <input
    type="text"
    value={newMessage}
    onChange={(e) => setNewMessage(e.target.value)}
    onKeyDown={(e) => e.key === "Enter" && !isSessionExpired && handleSend()}
    disabled={isSessionExpired || !wsConnected}
    placeholder={
      isSessionExpired
        ? "User session expired"
        : "Type a message…"
    }
    className={`flex-1 px-4 py-2 rounded-lg outline-none text-sm
      ${
        isSessionExpired
          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
          : "bg-gray-50 text-gray-800"
      }
    `}
  />

  {/* Send button */}
  <button
    onClick={handleSend}
    disabled={isSessionExpired || !newMessage.trim() || !wsConnected}
    className={`h-10 w-10 flex items-center justify-center rounded-lg transition-all
      ${
        isSessionExpired
          ? "bg-gray-300 cursor-not-allowed"
          : "bg-green-500 hover:bg-green-600"
      }
    `}
  >
    <span className="text-white text-lg">➤</span>
  </button>
</div>
</div>
            </div>
             </div>
          </>
        )}
      </div>

      {/* File input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,.doc,.docx,.txt"
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  )
}
